import React from "react";

/**
 * Shared presentation primitives for the tenant-detail "Übersicht" dashboard
 * (Slice O1a).
 *
 * WHY THIS FILE EXISTS. The rest of AdminTenantDetailPage draws its surfaces
 * from `tenant/tenantDetailStyles.js`, whose values are literal dark hex
 * (`bg-[#10121a]`, `text-[#edf0f7]`). Those DO render correctly in light mode —
 * `index.css` (:296ff) carries a compatibility layer that remaps each of them
 * under `html[data-theme="light"]` — but only because every single class was
 * hand-listed there. Building the dashboard on the semantic tokens the rest of
 * admin already uses (`bg-card`, `border-border`, `text-foreground`,
 * `text-muted-foreground`) keeps it out of that ledger entirely: new surfaces
 * theme themselves instead of needing another override entry.
 *
 * The card shape, the tinted icon chip and the accent light/dark PAIRS are
 * taken from `components/admin/companyOverview/OverviewKpiCard.jsx` so this is
 * the SAME visual language, not a second one. `OverviewKpiCard` itself is a
 * one-big-number KPI tile and does not fit a card that carries a field grid, a
 * checklist or a footer action — hence a sibling shell rather than a fork.
 *
 * Presentation only: nothing here fetches, derives or decides.
 */

/**
 * Icon-chip accents, given as light/dark pairs — a single light-only value
 * would be a white square on the dark page. Same values as OverviewKpiCard's
 * ACCENTS (which is module-private there); only the hues this surface uses.
 */
const ACCENTS = {
  blue: "bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400",
  emerald:
    "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400",
  amber: "bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400",
  violet:
    "bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-400",
};

/** Status-pill tones. Text carries the meaning; colour only reinforces it. */
const PILL_TONES = {
  emerald:
    "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  sky: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300",
  amber:
    "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  rose: "border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300",
  slate:
    "border-border bg-muted text-muted-foreground",
};

/** An em dash, never an empty cell and never "undefined". */
export function show(value) {
  return value == null || String(value).trim() === "" ? "—" : value;
}

/* ── Icons ──────────────────────────────────────────────────────────────
   Inline SVG rather than a dependency: seven small glyphs do not justify an
   icon package, and `currentColor` lets the accent chip colour them. */

const iconProps = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
};

export function UserIcon({ className = "h-4 w-4" }) {
  return (
    <svg {...iconProps} className={className}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

export function MapPinIcon({ className = "h-4 w-4" }) {
  return (
    <svg {...iconProps} className={className}>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

export function HomeIcon({ className = "h-4 w-4" }) {
  return (
    <svg {...iconProps} className={className}>
      <path d="M3 9.5 12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" />
      <path d="M9 21v-7h6v7" />
    </svg>
  );
}

export function ShieldIcon({ className = "h-4 w-4" }) {
  return (
    <svg {...iconProps} className={className}>
      <path d="M12 3l7 3v5.5c0 4.6-3 8.2-7 9.5-4-1.3-7-4.9-7-9.5V6z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

export function ReceiptIcon({ className = "h-4 w-4" }) {
  return (
    <svg {...iconProps} className={className}>
      <path d="M5 3h14v18l-3-2-3 2-3-2-3 2z" />
      <path d="M9 8h6M9 12h6" />
    </svg>
  );
}

export function FolderIcon({ className = "h-4 w-4" }) {
  return (
    <svg {...iconProps} className={className}>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </svg>
  );
}

export function ClipboardIcon({ className = "h-4 w-4" }) {
  return (
    <svg {...iconProps} className={className}>
      <path d="M9 4h6v3H9z" />
      <path d="M9 5.5H7a2 2 0 0 0-2 2V19a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7.5a2 2 0 0 0-2-2h-2" />
      <path d="m9 13 2 2 4-4" />
    </svg>
  );
}

export function CheckIcon({ className = "h-3.5 w-3.5" }) {
  return (
    <svg {...iconProps} className={className}>
      <path d="m5 13 4 4L19 7" />
    </svg>
  );
}

export function ChevronRightIcon({ className = "h-4 w-4" }) {
  return (
    <svg {...iconProps} className={className}>
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

export function CopyIcon({ className = "h-3.5 w-3.5" }) {
  return (
    <svg {...iconProps} className={className}>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15V5a2 2 0 0 1 2-2h10" />
    </svg>
  );
}

/* ── Card shell ─────────────────────────────────────────────────────────── */

/**
 * One dashboard card: tinted icon chip, title, optional header action or
 * status pill, body, optional footer action.
 *
 * `footer` sits below a hairline divider and is full-width by contract — every
 * card's "open the tab that owns this" control looks the same, because none of
 * them is the primary action of the page.
 */
export function OverviewCard({
  title,
  icon,
  accent = "blue",
  action,
  children,
  footer,
  testId,
  className = "",
}) {
  const chip = ACCENTS[accent] || ACCENTS.blue;
  return (
    <section
      data-testid={testId}
      className={`flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm ${className}`}
    >
      <div className="flex items-start justify-between gap-3 px-5 pt-5">
        <div className="flex min-w-0 items-center gap-3">
          {icon ? (
            <span
              aria-hidden
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${chip}`}
            >
              {icon}
            </span>
          ) : null}
          <h3 className="m-0 truncate text-[15px] font-semibold tracking-tight text-card-foreground">
            {title}
          </h3>
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>

      <div className="flex-1 px-5 pb-5 pt-4">{children}</div>

      {footer ? (
        <div className="border-t border-border px-5 py-3">{footer}</div>
      ) : null}
    </section>
  );
}

/** The card's footer control — full width, chevron, one shape everywhere. */
export function CardCta({ onClick, children, testId, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className="flex w-full items-center justify-between gap-2 rounded-lg border border-border bg-card px-3 py-2 text-[13px] font-medium text-card-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
    >
      <span className="truncate">{children}</span>
      <ChevronRightIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
    </button>
  );
}

/** A discreet text action, e.g. the card-header "Bearbeiten". */
export function CardAction({ onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="rounded-lg border border-border bg-card px-3 py-1.5 text-[12.5px] font-medium text-card-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
    >
      {children}
    </button>
  );
}

/** Small status badge. `tone` is semantic, never the card accent. */
export function StatusPill({ tone = "slate", children, testId }) {
  const cls = PILL_TONES[tone] || PILL_TONES.slate;
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${cls}`}
    >
      {children}
    </span>
  );
}

/* ── Field grid ─────────────────────────────────────────────────────────── */

/** Label above value. `mono` for identifiers and figures that line up. */
export function Field({ label, value, mono = false }) {
  const empty = value == null || String(value).trim() === "";
  return (
    <div className="min-w-0">
      <dt className="text-[11.5px] leading-tight text-muted-foreground">
        {label}
      </dt>
      <dd
        className={`m-0 mt-1 break-words text-[13.5px] ${
          empty ? "text-muted-foreground" : "text-card-foreground"
        } ${mono ? "font-mono text-[12.5px]" : ""}`}
      >
        {empty ? "—" : value}
      </dd>
    </div>
  );
}

/** The responsive field grid the profile card uses: 1 → 2 → 4 columns. */
export function FieldGrid({ children }) {
  return (
    <dl className="m-0 grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
      {children}
    </dl>
  );
}

/**
 * Label left, value right — the meta list under the address block.
 *
 * Which side yields when the row is too narrow depends on the value. Short
 * values (a date, a count) never need the space, so the LABEL may truncate. A
 * `mono` value is an identifier — a 36-character UUID at 412px leaves the label
 * as "Mi…", which names nothing — so there the label is fixed and the id wraps
 * instead. An identifier that wraps is still readable; a label that vanishes is
 * not.
 */
export function MetaRow({ label, value, icon, mono = false, action }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2">
      <span
        className={`flex items-center gap-2 pt-px text-[12.5px] text-muted-foreground ${
          mono ? "shrink-0" : "min-w-0"
        }`}
      >
        {icon ? (
          <span aria-hidden className="shrink-0 text-muted-foreground">
            {icon}
          </span>
        ) : null}
        <span className={mono ? "" : "truncate"}>{label}</span>
      </span>
      <span
        className={`flex items-center gap-1.5 ${mono ? "min-w-0" : "shrink-0"}`}
      >
        <span
          className={`text-[13px] font-medium text-card-foreground ${
            mono ? "break-all text-right font-mono text-[10.5px] leading-relaxed" : ""
          }`}
        >
          {show(value)}
        </span>
        {action}
      </span>
    </div>
  );
}

/** The one big figure a card is about. */
export function BigValue({ children, testId }) {
  return (
    <div
      data-testid={testId}
      className="text-[26px] font-semibold tracking-tight text-card-foreground"
    >
      {children}
    </div>
  );
}

/**
 * A checklist row: done → green check + date, otherwise a hollow marker and a
 * muted "ausstehend". Never a check without a proven date behind it.
 */
export function CheckRow({ label, done, date, testId }) {
  return (
    <div
      data-testid={testId}
      className="flex items-center justify-between gap-3 py-2.5"
    >
      <span className="flex min-w-0 items-center gap-2.5">
        {done ? (
          <span
            aria-hidden
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400"
          >
            <CheckIcon />
          </span>
        ) : (
          <span
            aria-hidden
            className="h-5 w-5 shrink-0 rounded-full border border-dashed border-border"
          />
        )}
        {/* Wraps rather than truncates: in the 2fr column a card is ~300px wide,
            where "Übergabeprotokoll unterzeichnet" does not fit on one line, and
            a cut word states less than a second line costs. */}
        <span className="min-w-0 text-[13px] leading-snug text-card-foreground">
          {label}
        </span>
      </span>
      <span className="shrink-0 pt-px text-[12.5px] text-muted-foreground">
        {done ? show(date) : "ausstehend"}
      </span>
    </div>
  );
}

/** "There is nothing here", in one shape across the dashboard. */
export function CardEmptyState({ title, hint, testId }) {
  return (
    <div
      data-testid={testId}
      className="flex flex-col items-center gap-2 px-4 py-8 text-center"
    >
      <span
        aria-hidden
        className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400"
      >
        <CheckIcon className="h-6 w-6" />
      </span>
      <p className="m-0 mt-1 text-[14px] font-semibold text-card-foreground">
        {title}
      </p>
      {hint ? (
        <p className="m-0 max-w-[42ch] text-[12.5px] leading-snug text-muted-foreground">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/** A neutral "no data" body — no green check, because nothing was achieved. */
export function CardNeutralState({ text, testId }) {
  return (
    <p
      data-testid={testId}
      className="m-0 py-2 text-[13px] leading-snug text-muted-foreground"
    >
      {text}
    </p>
  );
}

/** Hairline divider between blocks inside one card. */
export function CardDivider() {
  return <div className="my-4 h-px bg-border" aria-hidden />;
}
